import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Plus, X, Stethoscope, Home, Heart, Activity, MapPin } from "lucide-react";

const STATUS_OPTIONS = [
  { v: "open", l: "Open (needs help)" },
  { v: "at_clinic", l: "At Vet" },
  { v: "in_foster", l: "In Foster" },
  { v: "adopted", l: "Adopted ❤" },
  { v: "deceased", l: "Rest In Peace" },
];

export default function NgoRescueCases() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [vets, setVets] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => axios.get(`${API}/ngo/rescue-cases`).then(r => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); axios.get(`${API}/vets`).then(r => setVets(r.data)); }, []);

  if (user?.role !== "ngo") return (
    <div className="bg-surface border border-line rounded-3xl p-8 text-center">
      <h2 className="font-heading text-2xl font-extrabold">NGO account required</h2>
      <p className="text-ink-soft mt-2">Onboard your NGO from /app/partner to manage rescue cases.</p>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="ngo-rescue-cases-page">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold">My NGO · Rescue Cases</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Pipeline</h1>
          <p className="text-ink-soft text-sm">Street → Vet → Foster → Adoption. Each case is one life.</p>
        </div>
        <button onClick={() => setCreating(true)} data-testid="new-case-btn" className="bg-brand text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5">
          <Plus className="w-4 h-4"/> New rescue case
        </button>
      </header>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(c => (
          <article key={c.rescue_case_id} data-testid={`my-case-${c.rescue_case_id}`} onClick={() => setEditing(c)} className="cursor-pointer bg-surface border border-line rounded-3xl overflow-hidden shadow-soft hover:-translate-y-1 transition">
            <img src={c.photo} alt={c.pet_name} className="w-full h-32 object-cover"/>
            <div className="p-4">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-lg font-extrabold">{c.pet_name}</h3>
                <span className="text-[10px] font-bold uppercase tracking-wider bg-surface-muted rounded-full px-2 py-0.5">{c.status}</span>
              </div>
              <div className="text-xs text-ink-soft mt-1">{c.species} · {c.breed}</div>
              <div className="text-xs text-ink-soft mt-2 inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{c.intake_location}</div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <Mini icon={Stethoscope} active={!!c.vet_id} label="Vet"/>
                <Mini icon={Home} active={!!c.foster_id} label="Foster"/>
                <Mini icon={Heart} active={c.status === "adopted"} label="Adopt"/>
              </div>
            </div>
          </article>
        ))}
      </div>
      {items.length === 0 && (
        <div className="bg-surface border border-dashed border-line rounded-3xl p-10 text-center text-ink-soft">No cases yet — click "New rescue case" to log your first one.</div>
      )}
      {creating && <CreateCaseModal onClose={() => { setCreating(false); load(); }}/>}
      {editing && <EditCaseModal c={editing} vets={vets} onClose={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function Mini({ icon: Icon, active, label }) {
  return (
    <div className={`rounded-xl p-2 ${active ? "bg-brand/10 text-brand" : "bg-surface-muted text-ink-soft"}`}>
      <Icon className="w-4 h-4 mx-auto"/>
      <div className="text-[9px] uppercase tracking-wider font-bold mt-1">{label}</div>
    </div>
  );
}

function CreateCaseModal({ onClose }) {
  const [form, setForm] = useState({ pet_name: "", species: "dog", breed: "", age_estimate: "", gender: "", photo: "", intake_location: "", description: "", condition: "injured", estimated_treatment_cost: 0 });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    await axios.post(`${API}/ngo/rescue-cases`, form);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-lg border border-line shadow-soft max-h-[90vh] overflow-y-auto" data-testid="create-case-form">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-extrabold">Log a rescue</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Pet name (or unique tag)"><input data-testid="case-name" required value={form.pet_name} onChange={e => set("pet_name", e.target.value)} className="inp"/></Field>
          <Field label="Species">
            <select value={form.species} onChange={e => set("species", e.target.value)} className="inp">
              <option value="dog">Dog</option><option value="cat">Cat</option><option value="rabbit">Rabbit</option><option value="bird">Bird</option><option value="other">Other</option>
            </select>
          </Field>
          <Field label="Breed"><input value={form.breed} onChange={e => set("breed", e.target.value)} className="inp"/></Field>
          <Field label="Age estimate"><input placeholder="e.g. Puppy ~3mo" value={form.age_estimate} onChange={e => set("age_estimate", e.target.value)} className="inp"/></Field>
          <Field label="Condition">
            <select value={form.condition} onChange={e => set("condition", e.target.value)} className="inp">
              {["injured", "sick", "abandoned", "malnourished", "healthy"].map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Treatment cost (₹)"><input type="number" value={form.estimated_treatment_cost} onChange={e => set("estimated_treatment_cost", Number(e.target.value))} className="inp"/></Field>
        </div>
        <Field label="Intake location"><input required value={form.intake_location} onChange={e => set("intake_location", e.target.value)} className="inp"/></Field>
        <Field label="Story"><textarea required rows={3} value={form.description} onChange={e => set("description", e.target.value)} className="inp"/></Field>
        <Field label="Photo URL (optional)"><input placeholder="https://…" value={form.photo} onChange={e => set("photo", e.target.value)} className="inp"/></Field>
        <button data-testid="submit-case" className="mt-4 w-full bg-brand text-white rounded-full py-3 font-bold">Create case</button>
        <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.5rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.inp:focus{border-color:#FF6B35;}`}</style>
      </form>
    </div>
  );
}

function EditCaseModal({ c, vets, onClose }) {
  const [status, setStatus] = useState(c.status);
  const [vetId, setVetId] = useState(c.vet_id || "");
  const [note, setNote] = useState("");
  const [treatment, setTreatment] = useState(c.treatment_notes || "");
  const [publishing, setPublishing] = useState(false);
  const save = async () => {
    const payload = { status: status !== c.status ? status : undefined, vet_id: vetId !== (c.vet_id || "") ? vetId : undefined, note: note || undefined, treatment_notes: treatment !== (c.treatment_notes || "") ? treatment : undefined };
    await axios.patch(`${API}/ngo/rescue-cases/${c.rescue_case_id}`, payload);
    onClose();
  };
  const publishForAdoption = async () => {
    if (!window.confirm(`Publish ${c.pet_name} as an adoption listing? Pet parents will see this in /app/adoption.`)) return;
    setPublishing(true);
    try {
      const r = await axios.post(`${API}/adoption/list`, {
        name: c.pet_name,
        species: c.species,
        breed: c.breed || c.species,
        age_years: c.age_years || 1,
        gender: c.gender || "male",
        description: c.notes || c.treatment_notes || `${c.pet_name} is a rescue ready for a loving home.`,
        photo: c.photo,
        city: c.intake_location?.split(",").pop()?.trim() || "India",
        listing_type: "ngo",
        vaccinated: !!c.vaccinated,
        sterilized: !!c.sterilized,
        adoption_fee: 0,
      });
      // Link adoption listing to rescue case
      await axios.patch(`${API}/ngo/rescue-cases/${c.rescue_case_id}`, {
        adoption_listing_id: r.data.listing_id,
        status: "ready_for_adoption",
        note: `Published for adoption — listing ${r.data.listing_id}`,
      });
      alert("Published! Pet parents can now apply.");
      onClose();
    } finally { setPublishing(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-lg border border-line shadow-soft max-h-[90vh] overflow-y-auto" data-testid="edit-case-form">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-xl font-extrabold">Update {c.pet_name}</h3>
          <button onClick={onClose}><X className="w-5 h-5"/></button>
        </div>
        <Field label="Status">
          <select data-testid="edit-status" value={status} onChange={e => setStatus(e.target.value)} className="inp">
            {STATUS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </Field>
        <Field label="Assign Pro-bono Vet">
          <select data-testid="edit-vet" value={vetId} onChange={e => setVetId(e.target.value)} className="inp">
            <option value="">— None —</option>
            {vets.map(v => <option key={v.vet_id} value={v.vet_id}>{v.name} · {v.clinic}</option>)}
          </select>
        </Field>
        <Field label="Treatment notes (optional)">
          <textarea rows={3} value={treatment} onChange={e => setTreatment(e.target.value)} className="inp"/>
        </Field>
        <Field label="Timeline note">
          <input placeholder="e.g. Surgery completed successfully" value={note} onChange={e => setNote(e.target.value)} className="inp"/>
        </Field>
        {c.timeline?.length > 0 && (
          <div className="mt-3 bg-surface-muted/50 rounded-2xl p-3">
            <div className="text-xs uppercase tracking-wider font-bold text-ink-soft mb-2 inline-flex items-center gap-1"><Activity className="w-3 h-3"/>Recent journey</div>
            <div className="space-y-1.5 text-xs">
              {c.timeline.slice().reverse().slice(0, 4).map((t, i) => (
                <div key={i}><span className="font-bold">{t.note}</span> <span className="text-ink-soft">— {new Date(t.at).toLocaleDateString()}</span></div>
              ))}
            </div>
          </div>
        )}
        <button onClick={save} data-testid="save-case" className="mt-4 w-full bg-brand text-white rounded-full py-3 font-bold">Save update</button>
        {!c.adoption_listing_id && c.status !== "adopted" && (
          <button onClick={publishForAdoption} disabled={publishing} data-testid="publish-adoption" className="mt-2 w-full bg-leaf text-ink rounded-full py-2.5 font-bold inline-flex items-center justify-center gap-1.5 hover:bg-brand hover:text-white disabled:opacity-60">
            <Heart className="w-4 h-4"/> {publishing ? "Publishing…" : "Publish for adoption"}
          </button>
        )}
        {c.adoption_listing_id && (
          <div className="mt-2 text-center text-xs text-brand-green font-bold">✓ Published as adoption listing</div>
        )}
        <style>{`.inp{width:100%;border:1px solid #EAE0D5;border-radius:14px;padding:0.5rem 0.9rem;background:#FFFDF7;font-family:'Nunito';font-weight:600;outline:none;}.inp:focus{border-color:#FF6B35;}`}</style>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block mt-3">
      <span className="text-[11px] uppercase tracking-wider font-bold text-ink-soft">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
